import type { FormHTMLAttributes, ReactNode } from "react";
import { Button } from "./button/button";

export interface FormCardHeader {
  icon?: ReactNode;
  title?: string;
  subtitle?: string;
}

export interface FormCardProps extends Omit<
  FormHTMLAttributes<HTMLFormElement>,
  "onSubmit"
> {
  header: FormCardHeader;

  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  children: ReactNode;

  submitButtonText?: string;
  submitButtonLoadingText?: string;
  isLoading?: boolean;
  submitButtonDisabled?: boolean;
  /** Acción secundaria opcional, renderizada bajo el botón de submit. */
  footerExtra?: ReactNode;

  showCardWrapper?: boolean;
  /** Cuando true, el card usa overflow-visible (p. ej. para dropdowns/DatePicker que no se recorten). */
  overflowVisible?: boolean;
  className?: string;
  formClassName?: string;
  gap?: "sm" | "md" | "lg";
}

export const FormCard = ({
  header,
  onSubmit,
  children,
  submitButtonText,
  submitButtonLoadingText,
  isLoading = false,
  submitButtonDisabled = false,
  footerExtra,
  showCardWrapper = true,
  overflowVisible = false,
  className = "",
  formClassName = "",
  gap = "md",
  ...formProps
}: FormCardProps) => {
  const displayTitle = header.title || "";
  const displaySubtitle = header.subtitle || "";
  const displaySubmitText = submitButtonText || "";
  const displayLoadingText = submitButtonLoadingText || displaySubmitText;

  const gapClasses = {
    sm: "gap-2",
    md: "gap-3",
    lg: "gap-4",
  };

  const content = (
    <div className="relative flex flex-col h-full">
      <div className="mb-4 pb-3 border-b border-global-primary">
        <div className="flex items-center gap-3">
          {header.icon && (
            <div className="w-8 h-8 btn-global-primary rounded-lg flex items-center justify-center shadow-global-button ring-1 ring-global-accent shrink-0">
              {header.icon}
            </div>
          )}
          <div className="min-w-0">
            <h2 className="text-title-card">{displayTitle}</h2>
            {displaySubtitle && (
              <p className="text-size-2xs text-global-subtitle">
                {displaySubtitle}
              </p>
            )}
          </div>
        </div>
      </div>

      <form
        className={`flex flex-col grow ${gapClasses[gap]} ${formClassName}`.trim()}
        onSubmit={onSubmit}
        {...formProps}
      >
        {children}

        {displaySubmitText && (
          <Button
            type="submit"
            isLoading={isLoading}
            text={displaySubmitText}
            loadingText={displayLoadingText}
            fullWidth
            disabled={submitButtonDisabled}
          />
        )}

        {footerExtra}
      </form>
    </div>
  );

  if (!showCardWrapper) {
    return <div className={className}>{content}</div>;
  }

  return (
    <div
      className={`group relative card-compact hover:shadow-global-card-hover transition-all duration-300 ${overflowVisible ? "overflow-visible" : "overflow-hidden"} ${className}`.trim()}
    >
      <div className="absolute inset-0 gradient-accent-overlay opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
      {content}
    </div>
  );
};
